/**
 * Which plan the plan-selection screen recommends (founder QA F3, 2026-10-16).
 *
 * A user whose intake says mentor coach is pointed at Mentor Coach (tier3),
 * the only plan with the Mentees workspace; everyone else keeps the
 * Chasing Credentials (tier2) recommendation. A landing-page preselection
 * (`?plan=`) keeps the highlight; the "Recommended" badge follows the role.
 */
import type { IntakeSummary } from '../types';
import { isMentorProfile } from './postCheckoutRoute';

export type PlanTierId = 'tier1' | 'tier2' | 'tier3';

export function recommendedTierFor(
  summary: Pick<IntakeSummary, 'profile_type' | 'development_plan'> | null | undefined,
): PlanTierId {
  return isMentorProfile(summary) ? 'tier3' : 'tier2';
}

export interface PlanCardEmphasis {
  /** Primary border and tint. */
  highlighted: boolean;
  /** The "Recommended" badge. */
  recommendedBadge: boolean;
  /** The "Your pick" chip (landing preselection that is not also the recommendation). */
  preselectedChip: boolean;
}

/** `recommendedTier` is null while the intake is still loading, so no badge flashes on the wrong card. */
export function planCardEmphasis(
  planId: string,
  recommendedTier: PlanTierId | null,
  preselectedTier: string | null | undefined,
): PlanCardEmphasis {
  const recommended = planId === recommendedTier;
  const preselected = Boolean(preselectedTier) && planId === preselectedTier;
  return {
    highlighted: preselectedTier ? preselected : recommended,
    recommendedBadge: recommended,
    preselectedChip: preselected && !recommended,
  };
}

/** Locale keys (under `plans.mentorUpsell`) listing what only Mentor Coach gives a mentor. */
export function mentorCoachOnlyFeatureKeys(): string[] {
  // Server truth only: MENTOR_ENGAGEMENTS needs tier3 (entitlements._MIN_TIER) and sending the forms is
  // part of it; tier3 has 10 analyses a month vs 5 (plan_entitlements.PLAN_LIMITS). MCS preparation is
  // not listed: Feature.MCS_PREP has no minimum tier, so the server does not reserve it for tier3.
  return ['mentees', 'sendForms', 'analyses'];
}

export type MentorPlanNote = { kind: 'onlyIn'; keys: string[] } | { kind: 'menteesNotIncluded' } | null;

/** What a plan card adds for a mentor: the tier3 exclusives, or a muted "Mentees not included". */
export function mentorPlanNote(planId: string, recommendedTier: PlanTierId | null): MentorPlanNote {
  if (recommendedTier !== 'tier3') return null;
  if (planId === 'tier3') return { kind: 'onlyIn', keys: mentorCoachOnlyFeatureKeys() };
  if (planId === 'tier1' || planId === 'tier2') return { kind: 'menteesNotIncluded' };
  return null;
}
